import { brandConfig } from '../../../../config/brands';
import { useCreditRates } from '../../../../features/organizations/hooks';
import { NotifyToggleSwitch } from '../../../notifications/NotifyToggleSwitch';
import type { ScenarioAssetStatus, AssetStatus } from '../useExpressMode';
import styles from '../ExpressModeModal.module.css';

// Seedance 2.0 Mini 720p の credit_rate_config resource_type (秒単価)
const RESOURCE_TYPE_SEEDANCE_MINI_720 = 'seedance-2-mini-720p';

interface ScenarioItem {
  id: string;
  title: string;
}

interface StepGeneratingProps {
  /** Step 3: シナリオ生成中ローディング */
  isGeneratingScenarios: boolean;
  /** Step 4: 絵コンテ生成中 */
  isAutoGenerating: boolean;
  /** 絵コンテ生成が完了し選択できる状態か */
  storyboardsReady: boolean;
  scenarios: ScenarioItem[];
  scenarioAssetStatus: Record<string, ScenarioAssetStatus>;
  /** 生成予定の案数。シナリオ生成完了前にスケルトンを並べる枚数。 */
  expectedCount?: number;
  /** 動画化する案として選択中の id */
  selectedIds: string[];
  /** 拡大プレビュー中の id (PC=右ペイン / スマホ=全画面) */
  focusedId?: string | null;
  /** Seedance 出力尺 (秒・4..15)。選択案ごとの動画コスト見積りに使う */
  videoDurationSec?: number;
  aspectRatio?: '16:9' | '9:16';
  onToggleSelect: (scenarioId: string) => void;
  /** プレビュー対象を切り替える (PC=右ペイン)。ホバー / キーボードフォーカスで呼ぶ。 */
  onPreview: (scenarioId: string) => void;
  /** モバイルで絵コンテを全画面表示する (ホバー不可のためのタップ導線)。 */
  onOpenMobilePreview: (scenarioId: string) => void;
  onGenerateVideo: () => void;
}

export function StepGenerating({
  isGeneratingScenarios,
  isAutoGenerating,
  storyboardsReady,
  scenarios,
  scenarioAssetStatus,
  expectedCount = 0,
  selectedIds,
  focusedId,
  videoDurationSec = 15,
  onToggleSelect,
  onPreview,
  onOpenMobilePreview,
  onGenerateVideo,
}: StepGeneratingProps) {
  const { data: creditRatesData } = useCreditRates();

  const selectedCount = selectedIds.length;
  // 全案の完了 (storyboardsReady) を待たず、選択済みの中に絵コンテ完成 (done) が 1 つでもあれば
  // 動画生成に進める。選択は done のカードのみ可能なので selectedCount>0 ≒ done だが念のため確認。
  // 絵コンテ生成中 (isAutoGenerating=true) でも進めたいので isAutoGenerating は条件に含めない
  // (動画フェーズでは StepGenerating は表示されず StepExport に切り替わるため二重起動の懸念なし)。
  const hasSelectedDone = selectedIds.some(
    (id) => scenarioAssetStatus[id]?.storyboardStatus === 'done',
  );
  const canGenerate = hasSelectedDone;

  // 選択案の動画生成コスト見積り = 選択数 × Seedance秒単価 × 出力尺。
  const seedanceRate = creditRatesData?.rates.find(
    (r) => r.service_type === 'video_generation' && r.resource_type === RESOURCE_TYPE_SEEDANCE_MINI_720,
  );
  const videoPointCost = (seedanceRate?.credits_per_unit ?? 0) * videoDurationSec * selectedCount;

  // 生成中 (シナリオ/絵コンテ) は、実カードに足りない分を生成予定数までスケルトンで埋める。
  const showSkeletons = isGeneratingScenarios || (isAutoGenerating && !storyboardsReady);
  const skeletonCount = showSkeletons ? Math.max(0, expectedCount - scenarios.length) : 0;

  const title = storyboardsReady
    ? '絵コンテを選択'
    : isGeneratingScenarios
      ? `${brandConfig.labels.scenarioGenerate}中`
      : '絵コンテを生成中';
  const subtitle = storyboardsReady
    ? '動画化する案を選択してください（複数可）。'
    : isGeneratingScenarios
      ? 'シナリオを生成しています…（1〜2分ほどかかります）'
      : '絵コンテの生成には数分かかることがあります。';

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>{title}</h2>
      {/* サブタイトル行。生成中は完了通知トグルを同じ行の右端に出す (別タブ待機向け)。 */}
      <div className={`${styles.generatingHeaderRow} ${styles.notifyTogglePurple}`}>
        <p className={styles.loadingSubMessage}>{subtitle}</p>
        {!storyboardsReady && <NotifyToggleSwitch variant="panel" />}
      </div>

      <div className={styles.storyboardSelectGrid}>
        {scenarios.map((scenario, idx) => {
          const st = scenarioAssetStatus[scenario.id] || { storyboardStatus: 'pending' as AssetStatus, videoStatus: 'pending' as AssetStatus };
          const sbStatus = st.storyboardStatus;
          const selectable = sbStatus === 'done';
          const selected = selectable && selectedIds.includes(scenario.id);
          const focused = selectable && focusedId === scenario.id;
          return (
            <div
              key={scenario.id}
              className={`${styles.storyboardSelectCard} ${selected ? styles.storyboardSelectCardSelected : ''} ${focused ? styles.storyboardSelectCardFocused : ''} ${selectable ? styles.storyboardSelectCardClickable : ''}`}
              style={!selectable && sbStatus === 'failed' ? { opacity: 0.5 } : undefined}
              // ホバー / キーボードフォーカスで右パネルのプレビューを切り替える
              // (カード外に出ても最後の表示は残す = onMouseLeave なし)。
              onMouseEnter={() => selectable && onPreview(scenario.id)}
              onFocus={() => selectable && onPreview(scenario.id)}
              // カード全体をクリック領域にして選択トグル。
              onClick={() => selectable && onToggleSelect(scenario.id)}
              role={selectable ? 'button' : undefined}
              tabIndex={selectable ? 0 : undefined}
              aria-pressed={selectable ? selected : undefined}
              onKeyDown={selectable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelect(scenario.id); }
              } : undefined}
            >
              <div className={`${styles.sceneThumb} ${styles.storyboardThumbButton} ${styles.storyboardThumbAspect}`}>
                {/* モバイル専用: ホバー不可のため絵コンテを全画面表示するズームボタン (PC では CSS で非表示) */}
                {selectable && (
                  <button
                    type="button"
                    className={styles.storyboardMobileZoom}
                    onClick={(e) => { e.stopPropagation(); onOpenMobilePreview(scenario.id); }}
                    aria-label="絵コンテを拡大表示"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>
                )}
                {sbStatus === 'done' && st.storyboardImageUrl ? (
                  <img src={st.storyboardImageUrl} alt="絵コンテ" className={styles.sceneThumbImg} />
                ) : sbStatus === 'generating' ? (
                  <div className={styles.sceneThumbLoading}>
                    <div className={styles.spinnerSmall} />
                    <span className={styles.sceneThumbStatusLabel}>絵コンテ生成中</span>
                  </div>
                ) : sbStatus === 'failed' ? (
                  <div className={styles.sceneThumbFailed}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </div>
                ) : (
                  <div className={`${styles.sceneThumbSkeleton} ${styles.skeletonPulse}`}>
                    <span className={styles.sceneThumbStatusLabel}>待機中</span>
                  </div>
                )}
              </div>
              {/* 絵コンテ右上端の選択状態バッジ (表示のみ。トグルはカード全体のクリックで行う) */}
              {selectable && (
                <span className={`${styles.storyboardStatusBadge} ${selected ? styles.storyboardStatusBadgeOn : ''}`}>
                  {selected ? '選択中' : '未選択'}
                </span>
              )}
              {/* タイトル (表示のみ。選択状態は右上のバッジで表す) */}
              <div className={`${styles.storyboardSelectToggle} ${selected ? styles.storyboardSelectToggleOn : ''}`}>
                <span className={styles.storyboardSelectTextCol}>
                  <span className={styles.storyboardSelectTitle}>{scenario.title || `案 ${idx + 1}`}</span>
                </span>
              </div>
            </div>
          );
        })}
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <div key={`sk-${i}`} className={styles.storyboardSelectCard}>
            <div className={`${styles.sceneThumb} ${styles.storyboardThumbAspect} ${styles.sceneThumbSkeleton} ${styles.skeletonPulse}`} />
            <div className={styles.storyboardSelectToggle} style={{ pointerEvents: 'none' }}>
              <span className={styles.storyboardSelectTextCol}>
                <span className={`${styles.storyboardSkeletonLine} ${styles.skeletonPulse}`} />
              </span>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.primaryButton}
        onClick={onGenerateVideo}
        disabled={!canGenerate}
        style={!canGenerate ? { opacity: 0.6 } : undefined}
      >
        <span className={styles.buttonMainRow}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
          {selectedCount > 0 ? `動画生成（${selectedCount}案）` : '動画生成'}
        </span>
        {selectedCount > 0 && videoPointCost > 0 && (
          <span className={styles.buttonCreditBadge}>
            {Math.floor(videoPointCost).toLocaleString()}ポイント
          </span>
        )}
      </button>
    </div>
  );
}
